/* eslint-disable no-param-reassign */
import { PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { DailyStatus } from 'services/api/models/SymptomTrackingDTO';
import resetAll from 'store/shared/resetAll';
import createSlice from 'utils/@reduxjs/toolkit';
import { ReminderTime, SymptomTrackingState } from './types';

export const initialState: SymptomTrackingState = {
  dailyStatuses: undefined,
  reminderTime: null,
  isPeriodReminderEnabled: false,
};

const slice = createSlice({
  name: 'symptomTracking',
  initialState,
  extraReducers: (builder: any) => builder.addCase(resetAll, () => initialState),
  reducers: {
    setDailyStatuses: (state, action: PayloadAction<DailyStatus[]>) => {
      state.dailyStatuses = action.payload;
    },
    addPeriodToEmptyDays: (state, action: PayloadAction<string[]>) => {
      const existingDays = new Set(
        state.dailyStatuses?.map(status => status.date).filter(date => date && action.payload.includes(date)),
      );
      // filter out days that are in the future
      const days = action.payload.filter(day => dayjs(day).isSameOrBefore(dayjs(), 'day'));

      // creating statuses for days that are not in the state
      const newStatuses: DailyStatus[] = days
        .filter(day => !existingDays.has(day))
        .map(day => ({
          date: day,
          general_state: null,
          menstruation_amount: 1,
          pain_level: null,
          symptoms: [],
          extra_symptoms: '',
          comment: '',
        }));
      state.dailyStatuses = [
        ...(state.dailyStatuses?.map(status =>
          // updating menstruation for days that are already in the state
          status.date && existingDays.has(status.date) ? { ...status, menstruation_amount: 1 } : status,
        ) || []),
        ...newStatuses,
      ].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    },
    removePeriodFromDay: (state, action: PayloadAction<string>) => {
      const date = action.payload;
      const existingDay = state.dailyStatuses?.find(status => status.date === date);
      if (existingDay) {
        if (existingDay.general_state === null) {
          state.dailyStatuses = state.dailyStatuses?.filter(status => status.date !== date);
        } else {
          state.dailyStatuses = state.dailyStatuses?.map(status =>
            status.date !== date ? status : { ...status, menstruation_amount: 0 },
          );
        }
      }
    },
    setReminderTime: (state, action: PayloadAction<ReminderTime | null>) => {
      state.reminderTime = action.payload;
    },
    setPeriodReminderEnabled: (state, action: PayloadAction<boolean>) => {
      state.isPeriodReminderEnabled = action.payload;
    },
  },
});

/**
 * `actions` will be used to trigger change in the state from where ever you want
 */
export const { actions: symptomTrackingActions, reducer: symptomTrackingReducer } = slice;
